import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductDetailModal from '../components/ProductDetailModal';
import SMMPanel from '../components/SMMPanel';
import { useLanguage } from '../context/LanguageContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import '../styles/theme.css';
import './StorePage.css';

const StorePage = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, featuredOnly]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        category: selectedCategory !== 'all' ? selectedCategory : '',
        featured: featuredOnly ? 'true' : ''
      });

      const response = await api.get(`/products?${params}`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(t('storeFailedToLoadProducts'));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  const handlePurchaseComplete = () => {
    // Optionally refresh products or show success message
    fetchProducts();
  };

  const categories = [
    { value: 'all', label: t('storeCategoryAll') },
    { value: 'music_album', label: t('storeCategoryMusicAlbum') },
    { value: 'song_catalog', label: t('storeCategorySongCatalog') },
    { value: 'beat', label: t('storeCategoryBeat') },
    { value: 'instrumental', label: t('storeCategoryInstrumental') },
    { value: 'sample_pack', label: t('storeCategorySamplePack') },
    { value: 'other', label: t('storeCategoryOther') }
  ];

  return (
    <div className="store-page">
      <Header />
      
      <main className="store-main">
        <div className="store-hero">
          <h1>{t('storeTitle')}</h1>
          <p>{t('storeSubtitle')}</p>
        </div>

        <SMMPanel />

        <div className="store-filters">
          <div className="category-filter">
            {categories.map(cat => (
              <button
                key={cat.value}
                className={`filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <label className="featured-toggle">
            <input
              type="checkbox"
              checked={featuredOnly}
              onChange={(e) => setFeaturedOnly(e.target.checked)}
            />
            <span>{t('storeFeaturedOnly')}</span>
          </label>
        </div>

        {loading ? (
          <div className="loading">{t('storeLoadingProducts')}</div>
        ) : (
          <div className="products-grid">
            {products.length === 0 ? (
              <div className="empty-state">
                <h3>{t('storeNoProductsTitle')}</h3>
                <p>{t('storeNoProductsDesc')}</p>
              </div>
            ) : (
              products.map((product) => (
                <div key={product._id} className="product-card">
                  {product.image && (
                    <div className="product-image">
                      <img src={product.image} alt={product.title} />
                      {product.isFeatured && (
                        <span className="featured-badge">Featured</span>
                      )}
                    </div>
                  )}
                  <div className="product-info">
                    <h3>{product.title}</h3>
                    <p className="product-category">{product.category.replace('_', ' ')}</p>
                    <p className="product-description">{product.description}</p>
                    <div className="product-footer">
                      <span className="product-price">
                        {product.price} {product.paymentCurrency || product.currency}
                        {product.files && product.files.length > 0 && (
                          <span style={{ fontSize: '0.75rem', display: 'block', color: '#888', marginTop: '0.25rem' }}>
                            {product.files.length} {product.files.length === 1 ? 'track' : 'tracks'}
                          </span>
                        )}
                      </span>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => handleProductClick(product)}
                      >
                        {t('storeViewDetails')}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      <Footer />

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          isOpen={showModal}
          onClose={handleCloseModal}
          onPurchaseComplete={handlePurchaseComplete}
        />
      )}
    </div>
  );
};

export default StorePage;
